'use client';

import { useState, useCallback, useEffect, useContext, useRef } from 'react';
import { getClasses } from '@/_utils/classnames';
import {
  Alert,
  Pill,
  Button,
  ButtonGroup,
  Flex,
  Heading,
  Icon,
  Text,
  Textarea,
  Dropdown,
} from '@/_components';
import styles from './Assistant.module.css';
import type { AssistantProps } from './Assistant.types';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { AGENT_API_URL } from '@/_libs/constants';
import { MessageRenderer } from './_components/MessageRenderer';
import { ChatHistoryPopup } from './_components/ChatHistoryPopup';
import { QuestionsLibraryPopup } from './_components/QuestionsLibraryPopup';
import type { MessageType } from '@/_types/ai';
import type { ResultListItem } from '@/_components/ResultList/ResultList.types';
import { LayoutContext } from '@/_contexts/LayoutProvider';
import { useAdminChatThreads } from '@/_contexts/admin/AdminChatThreadsProvider';
import type { ThinkingStatus } from './_components/MessageRenderer/_components/ThinkingIndicator/ThinkingIndicator';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';

export const STREAM_ABORT_TIMEOUT_MS = 300000;
export const NO_CONTENT_FALLBACK_TIMEOUT_MS = 60000;

export const Assistant: React.FC<AssistantProps> = () => {
  const { user, token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);
  const { isAssistantOpen, setIsAssistantOpen } = useContext(LayoutContext);
  const { preferences } = useContext(FirmSettingsContext);

  const [isAnimating, setIsAnimating] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isQuestionsLibraryOpen, setIsQuestionsLibraryOpen] = useState(false);

  // Dropdown menu items
  const menuItems: ResultListItem[] = [
    {
      value: 'questions-library',
      name: 'Questions Library',
      icon: 'list',
    },
    {
      value: 'delete-session',
      name: 'Delete',
      icon: 'trash',
      color: 'critical',
    },
  ];
  const {
    activeThreadId,
    createNewThread,
    getCurrentThread,
    addMessageToCurrentThread,
    updateMessageInCurrentThread,
    isHistoryOpen,
    toggleHistory,
    isInitialized,
    sessionExpired,
    setSessionExpired,
    deleteThread,
  } = useAdminChatThreads();

  const onToggle = () => {
    if (isAssistantOpen) {
      setIsAnimating(true);
      setTimeout(() => {
        setIsAssistantOpen(false);
        setIsAnimating(false);
      }, 240);
    } else {
      setIsAssistantOpen(true);
    }
  };

  const [message, setMessage] = useState('');
  const [messageFeedback, setMessageFeedback] = useState<{
    [key: string]: 'up' | 'down';
  }>({});
  const [messages, setMessages] = useState<MessageType[]>([
    {
      id: 'welcome-1',
      time: getCurrentTime(),
      content:
        'Welcome to Soraban Admin Assistant! I can help you manage clients, track questionnaires, stay on top of deadlines, and answer IRS-related questions for your firm.',
      examples: [
        'Show me all incomplete questionnaires with entity names',
        'Export all incomplete questionnaires to a spreadsheet',
        'What are the open items for Comprehensive Tax Filing for [entity name] in 2024?',
        'Which clients answered that they received a PPP loan in 2024?',
      ],
      role: 'assistant',
      isUser: false,
    },
  ]);
  const [isStreaming, setIsStreaming] = useState(false);
  const isStreamingRef = useRef(false);
  const [streamingMessageId, setStreamingMessageId] = useState<
    string | undefined
  >(undefined);
  const [thinkingStatus, setThinkingStatus] =
    useState<ThinkingStatus>('thinking');
  const streamController = useRef<AbortController | null>(null);
  const requestCounter = useRef(0);
  const streamingIndexRef = useRef<number | null>(null);
  const pendingContentRef = useRef<string>('');
  const rafUpdateIdRef = useRef<number>(0);

  const chatContainerRef = useRef<HTMLDivElement>(null);
  const rafScrollIdRef = useRef<number>(0);

  const scrollToBottom = useCallback((force: boolean = false) => {
    if (!chatContainerRef.current) return;

    const el = chatContainerRef.current;
    const threshold = 100; // px from bottom
    const nearBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
    if (!force && !nearBottom) return;
    if (rafScrollIdRef.current) cancelAnimationFrame(rafScrollIdRef.current);

    rafScrollIdRef.current = requestAnimationFrame(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop =
          chatContainerRef.current.scrollHeight;
      }
    });
  }, []);

  const startNewAssistantMessage = useCallback(
    (
      content = '',
      isStreaming = false,
      otherProps: Partial<MessageType> = {},
    ) => {
      const { content: _, ...restOfOtherProps } = otherProps;
      const newAssistantMessage: MessageType = {
        id: generateMessageId(),
        time: getCurrentTime(),
        content: isStreaming ? addStreamingCursor(content) : content,
        role: 'assistant',
        ...restOfOtherProps,
      };

      setMessages((prev) => {
        const next = [...prev, newAssistantMessage];
        if (isStreaming) {
          streamingIndexRef.current = next.length - 1;
        }
        return next;
      });

      // Also add to chat threads for history
      console.log('Adding assistant message to thread:', newAssistantMessage);
      addMessageToCurrentThread(newAssistantMessage);

      pendingContentRef.current = content;
      return newAssistantMessage.id;
    },
    [addMessageToCurrentThread],
  );

  function getCurrentTime() {
    const now = new Date();
    return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  function generateMessageId() {
    return `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  const handleSessionRefresh = useCallback(() => {
    // When session expires (401), the token is no longer valid
    // The only way to recover is to reload the page and re-authenticate
    window.location.reload();
  }, []);

  const handleMenuSelect = useCallback((item: ResultListItem) => {
    if (item.value === 'delete-session') {
      setShowDeleteConfirm(true);
    } else if (item.value === 'questions-library') {
      setIsQuestionsLibraryOpen(true);
    }
  }, []);

  const handleDeleteSession = useCallback(async () => {
    if (!activeThreadId) {
      addToast('error', 'No active session to delete.');
      return;
    }

    try {
      await deleteThread(activeThreadId);
      addToast('success', 'Session deleted successfully.');
      setShowDeleteConfirm(false);

      // Reset to empty chat
      setMessages([]);
      setMessage('');
      setIsStreaming(false);
      setStreamingMessageId(undefined);
      if (streamController.current) {
        streamController.current.abort();
        streamController.current = null;
      }
    } catch (error) {
      console.error('Failed to delete session:', error);
      addToast('error', 'Failed to delete session. Please try again.');
    }
  }, [activeThreadId, deleteThread, addToast]);

  const handleFeedback = (messageId: string, feedback: 'up' | 'down') => {
    setMessageFeedback((prev) => ({
      ...prev,
      [messageId]: feedback,
    }));
    addToast('success', 'Thanks for your feedback!');
  };

  const addStreamingCursor = (content: string): string => {
    return (content || '') + `<span class="${styles.streamingCursor}">_</span>`;
  };

  const removeStreamingCursor = (content: string): string => {
    if (!content) return '';
    return content.replace(
      `<span class="${styles.streamingCursor}">_</span>`,
      '',
    );
  };

  const flushStreamingUpdate = useCallback(() => {
    rafUpdateIdRef.current = 0;
    const latestContent = pendingContentRef.current;

    const i = streamingIndexRef.current;
    if (i == null) return;

    setMessages((prev) => {
      if (i < 0 || i >= prev.length) return prev;
      const next = prev.slice();
      const msg = next[i];
      const updatedContent = addStreamingCursor(latestContent || '');
      next[i] = { ...msg, content: updatedContent };

      // Sync the streaming update to thread provider
      setTimeout(() => {
        console.log('Syncing streaming update to thread:', {
          id: msg.id,
          content: updatedContent,
        });
        updateMessageInCurrentThread(msg.id, {
          content: updatedContent,
          updatedAt: new Date(),
        });
      }, 0);

      return next;
    });
  }, [updateMessageInCurrentThread]);

  const scheduleStreamingFlush = useCallback(() => {
    if (!rafUpdateIdRef.current) {
      rafUpdateIdRef.current = requestAnimationFrame(flushStreamingUpdate);
    }
    if (isStreamingRef.current) scrollToBottom(true);
  }, [flushStreamingUpdate, scrollToBottom]);

  const finalizeMessage = useCallback(
    (messageId: string, metadata: any, startTime: number) => {
      setMessages((prevMessages) =>
        prevMessages.map((msg) => {
          if (msg.id === messageId) {
            const finalContent = removeStreamingCursor(
              pendingContentRef.current || '',
            );

            const hasTable =
              (finalContent.includes('|') && finalContent.includes('\n|')) ||
              finalContent.includes('uncompleted questionnaires') ||
              finalContent.includes('incomplete questionnaires');
            let examples: string[] = [];
            if (finalContent.includes('You can also:')) {
              const examplesMatch = finalContent.match(
                /You can also:([\s\S]*?)(\n\n|$)/,
              );
              if (examplesMatch?.[1]) {
                examples = examplesMatch[1]
                  .split('\n-')
                  .map((ex: string) => ex.trim())
                  .filter(Boolean);
                if (examples[0]?.startsWith('-')) {
                  examples[0] = examples[0].substring(1).trim();
                }
              }
            }
            const updatedMessage = {
              ...msg,
              content: finalContent,
              examples: examples.length > 0 ? examples : undefined,
              isStructured: hasTable,
              questionnaireIds:
                metadata.questionnaireIds || msg.questionnaireIds,
              questionIds: metadata.questionIds || msg.questionIds,
              questionnairePairs:
                metadata.questionnairePairs || msg.questionnairePairs,
              questionnaireMetadata:
                metadata.questionnaireMetadata || msg.questionnaireMetadata,
              graphql_data: metadata.graphql_data || msg.graphql_data,
              responseDurationMs: Date.now() - startTime,
            };

            // Sync the finalized message to thread provider
            setTimeout(() => {
              console.log('Syncing finalized message to thread:', {
                id: messageId,
                content: finalContent,
              });
              updateMessageInCurrentThread(messageId, updatedMessage);
            }, 0);

            return updatedMessage;
          }
          return msg;
        }),
      );
    },
    [updateMessageInCurrentThread],
  );

  // ✅ FIXED: Reworked to handle chunked data events
  const processStreamingEvent = useCallback(
    (event: any, _messageId: string, finalMetadata: any) => {
      console.log('processStreamingEvent', event);
      switch (event?.type) {
        case 'AssistantMessage': {
          setThinkingStatus('thinking');
          const i = streamingIndexRef.current;
          if (i !== null) {
            setMessages((prev) => {
              if (i < 0 || i >= prev.length) return prev;
              const next = prev.slice();
              const msg = next[i];
              const finalContent = removeStreamingCursor(
                pendingContentRef.current || '',
              );
              next[i] = { ...msg, content: finalContent };
              return next;
            });
          }
          startNewAssistantMessage(event.data || '', true);
          break;
        }
        case 'TextChunk': {
          setThinkingStatus('thinking');
          const textChunk = event.data || '';
          if (typeof textChunk === 'string') {
            pendingContentRef.current += textChunk;
            scheduleStreamingFlush();
          }
          break;
        }

        case 'DataPayload': {
          setThinkingStatus('querying');
          const i = streamingIndexRef.current;
          if (i === null) break;

          setMessages((prev) => {
            const next = [...prev];
            const msg = next[i];
            if (!msg) return prev;

            const data = event.data || {};
            const {
              questionnaire_ids,
              questionnaire_metadata,
              questionnaire_question_pairs,
            } = data;

            let questionnaireIds = msg.questionnaireIds || [];
            if (Array.isArray(questionnaire_ids)) {
              questionnaireIds = [
                ...questionnaireIds,
                ...questionnaire_ids
                  .map((id: any) => Number(id))
                  .filter((id: number) => !isNaN(id)),
              ];
            }

            let questionnaireMetadata = msg.questionnaireMetadata || [];
            if (Array.isArray(questionnaire_metadata)) {
              questionnaireMetadata = [
                ...questionnaireMetadata,
                ...questionnaire_metadata,
              ];
            }

            let questionnairePairs = msg.questionnairePairs || [];
            if (Array.isArray(questionnaire_question_pairs)) {
              questionnairePairs = [
                ...questionnairePairs,
                ...questionnaire_question_pairs,
              ];
            }

            next[i] = {
              ...msg,
              questionnaireIds,
              questionnaireMetadata,
              questionnairePairs,
            };

            return next;
          });
          break;
        }

        case 'GraphQLData': {
          setThinkingStatus('analyzing');
          if (Array.isArray(event.data)) {
            finalMetadata.graphql_data = event.data;
          }
          break;
        }

        case 'DataEnd': {
          console.log('Data stream finished.');
          break;
        }

        case 'error': {
          console.error('Streaming error from backend:', event.content);
          pendingContentRef.current += `\n\nAn error occurred: ${event.content}`;
          scheduleStreamingFlush();
          break;
        }

        default:
          console.log('Ignoring unhandled streaming event type:', event?.type);
          break;
      }
    },
    [scheduleStreamingFlush, startNewAssistantMessage],
  );

  const handleStop = useCallback(() => {
    if (streamController.current) {
      streamController.current.abort();
      addToast('info', 'Generation stopped');
    }
  }, [addToast]);

  const handleSend = useCallback(async () => {
    if (!preferences?.ai_chat_enabled) return;

    if (isStreaming) {
      handleStop();
      return;
    }

    const currentRequest = ++requestCounter.current;
    if (!message.trim()) return;

    // Check if we have a session
    const currentThread = getCurrentThread();
    if (!currentThread?.sessionId) {
      addToast(
        'error',
        'No active chat session. Please wait for initialization to complete.',
      );
      console.error('No session ID found for the current thread.');
      return;
    }
    const { sessionId } = currentThread;

    pendingContentRef.current = '';

    const userInput = message.trim();
    const currentTime = getCurrentTime();
    const userMessage: MessageType = {
      id: generateMessageId(),
      time: currentTime,
      content: userInput,
      isUser: true,
      role: 'user',
    };

    setMessages((prevMessages) => [...prevMessages, userMessage]);
    console.log('Adding user message to thread:', userMessage);
    addMessageToCurrentThread(userMessage);

    const assistantStartTime = Date.now();
    const assistantMessageId = startNewAssistantMessage('', true);

    setIsStreaming(true);
    isStreamingRef.current = true;
    setStreamingMessageId(assistantMessageId);
    setThinkingStatus('searching');
    setMessage('');
    scrollToBottom(true);

    streamController.current = new AbortController();
    const timeoutId = setTimeout(
      () => streamController.current?.abort(),
      STREAM_ABORT_TIMEOUT_MS,
    );

    let hasReceivedContent = false;

    try {
      const response = await fetch(
        `${AGENT_API_URL}/sessions/${sessionId}/query`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            query: userInput,
            user_id: user?.id ? String(user.id) : '1',
            token,
          }),
          signal: streamController.current.signal,
        },
      );

      if (response.status === 401) {
        setSessionExpired(true);
        setMessages((prevMessages) =>
          prevMessages.map((msg) =>
            msg.id === assistantMessageId
              ? {
                  ...msg,
                  content:
                    'Your session has expired. Please refresh to continue.',
                  responseDurationMs: Date.now() - assistantStartTime,
                }
              : msg,
          ),
        );
        setIsStreaming(false);
        setStreamingMessageId(undefined);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      if (!response.body) throw new Error('No response body');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      const finalMetadata: any = {};

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let eventSeparatorIndex;
        while ((eventSeparatorIndex = buffer.indexOf('\n\n')) !== -1) {
          const eventString = buffer.substring(0, eventSeparatorIndex);
          buffer = buffer.substring(eventSeparatorIndex + 2);

          if (!eventString) continue;

          const lines = eventString.split('\n');
          let jsonData = '';
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              jsonData += line.substring(6).trim();
            }
          }

          if (jsonData) {
            try {
              const event = JSON.parse(jsonData);
              processStreamingEvent(event, assistantMessageId, finalMetadata);

              if (
                !hasReceivedContent &&
                event?.type === 'TextChunk' &&
                event.data
              ) {
                hasReceivedContent = true;
              }
            } catch (e) {
              console.error(
                'Failed to parse event JSON from SSE chunk:',
                jsonData,
                e,
              );
            }
          }
        }
      }

      finalizeMessage(assistantMessageId, finalMetadata, assistantStartTime);
    } catch (error) {
      if (currentRequest < requestCounter.current) {
        return;
      }
      const i = streamingIndexRef.current;
      if (error instanceof Error && error.name === 'AbortError') {
        if (i !== null) {
          setMessages((prevMessages) =>
            prevMessages.map((msg, index) =>
              index === i
                ? {
                    ...msg,
                    content:
                      removeStreamingCursor(pendingContentRef.current || '') ||
                      'Request was stopped.',
                    responseDurationMs: Date.now() - assistantStartTime,
                  }
                : msg,
            ),
          );
        }
        return;
      }

      if (i !== null) {
        setMessages((prevMessages) =>
          prevMessages.map((msg, index) =>
            index === i
              ? {
                  ...msg,
                  content: `I encountered an error: ${
                    error instanceof Error ? error.message : 'Unknown error'
                  }. Please try again.`,
                  responseDurationMs: Date.now() - assistantStartTime,
                }
              : msg,
          ),
        );
      }
      addToast('error', 'Error processing your query.', { error });
    } finally {
      if (currentRequest === requestCounter.current) {
        clearTimeout(timeoutId);
        setIsStreaming(false);
        isStreamingRef.current = false;
        setStreamingMessageId(undefined);
        streamController.current = null;
      }
    }
  }, [
    addToast,
    handleStop,
    isStreaming,
    message,
    processStreamingEvent,
    scrollToBottom,
    startNewAssistantMessage,
    token,
    finalizeMessage,
    getCurrentThread,
    addMessageToCurrentThread,
    preferences?.ai_chat_enabled,
    setSessionExpired,
    user,
  ]);

  const handleRegenerate = useCallback(
    async (lastUserMessage: string) => {
      if (isStreaming) {
        handleStop();
        return;
      }

      // Set the message to the last user message
      setMessage(lastUserMessage);

      // Call handleSend to regenerate
      await handleSend();
    },
    [handleSend, handleStop, isStreaming, setMessage],
  );

  const assistantClasses: string = getClasses(styles.assistantPanel, {
    [styles.assistantPanelEnter]: isAssistantOpen && !isAnimating,
    [styles.assistantPanelExit]: !isAssistantOpen && isAnimating,
    [styles.assistantPanelHidden]: !isAssistantOpen,
  });

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
  };

  // Initialize chat thread when component mounts or when active thread changes
  useEffect(() => {
    // Wait for the provider to be initialized before taking any action
    if (!isInitialized) {
      return;
    }

    const currentThread = getCurrentThread();
    if (currentThread) {
      // Only reload messages if we're not currently streaming
      // This prevents wiping out in-progress responses when reopening the assistant
      if (!isStreaming && !streamingMessageId) {
        const loadedMessages = currentThread.messages || [];

        // Filter out old welcome-2 message (legacy format)
        const cleanedMessages = loadedMessages.filter(
          (msg) => msg.id !== 'welcome-2',
        );

        setMessages(cleanedMessages);
      }
    } else {
      // If no thread exists, create a new one
      createNewThread();
    }
  }, [
    isInitialized,
    activeThreadId,
    createNewThread,
    getCurrentThread,
    isStreaming,
    streamingMessageId,
  ]);

  // Sync local messages back to thread provider when streaming completes
  useEffect(() => {
    if (!isStreaming && streamingMessageId) {
      // Find the message that was being streamed and sync it
      const completedMessage = messages.find(
        (msg) => msg.id === streamingMessageId,
      );
      if (completedMessage) {
        // Use setTimeout to defer the update until after the current render cycle
        setTimeout(() => {
          console.log('Syncing completed message to thread:', completedMessage);
          updateMessageInCurrentThread(streamingMessageId, completedMessage);
        }, 0);
      }
    }
  }, [isStreaming, streamingMessageId, messages, updateMessageInCurrentThread]);

  useEffect(() => {
    scrollToBottom(true);
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (isAssistantOpen) {
      setTimeout(() => scrollToBottom(true), 100);
    }
  }, [isAssistantOpen, scrollToBottom]);

  useEffect(() => {
    if (isAssistantOpen) {
      document.body.classList.add('assistant-open');
    } else {
      document.body.classList.remove('assistant-open');
    }
    return () => document.body.classList.remove('assistant-open');
  }, [isAssistantOpen]);

  useEffect(() => {
    return () => {
      streamController.current?.abort();

      if (rafUpdateIdRef.current) cancelAnimationFrame(rafUpdateIdRef.current);
      if (rafScrollIdRef.current) cancelAnimationFrame(rafScrollIdRef.current);
    };
  }, []);

  return (
    <div className={assistantClasses}>
      <Flex
        isYCentered={true}
        isBetween={true}
        gap={8}
        className={styles.assistantHeader}
      >
        <Flex isYCentered gap={12}>
          <div className={styles.headerIcon}>
            <Icon name="sparkle" size={20} color="white" />
          </div>
          <Flex isColumn gap={0}>
            <Flex isYCentered gap={8}>
              <Heading level={5} isSameLine={true}>
                Admin Assistant
              </Heading>
              <Pill label="BETA" intent="info" />
            </Flex>
            <Text type="tiny" color="secondary">
              AI-powered admin assistant
            </Text>
          </Flex>
        </Flex>
        <Flex isYCentered gap={2}>
          <Button
            value="Chat History"
            onClick={toggleHistory}
            iconLeft="clock"
            intent="ghost"
            size="small"
            aria-label="View chat history"
            className={isHistoryOpen ? styles.activeHistoryBtn : ''}
          />
          <Dropdown
            items={menuItems}
            onSelect={handleMenuSelect}
            isDisabled={!activeThreadId || sessionExpired}
          >
            <Button
              value="More options"
              iconLeft="dots-three"
              intent="ghost"
              size="small"
              isIconOnly={true}
              aria-label="More options"
            />
          </Dropdown>
          <Button
            value="Close"
            onClick={onToggle}
            iconLeft="x"
            intent="ghost"
            size="small"
            isIconOnly={true}
            aria-label="Close assistant"
          />
        </Flex>
      </Flex>

      <div className={styles.assistantContent}>
        <div className={styles.chatContainer} ref={chatContainerRef}>
          {sessionExpired ? (
            <div className={styles.sessionExpiredContainer}>
              <Flex isColumn={true} gap={12} isCentered={true}>
                <Alert
                  type="warning"
                  title="Session expired"
                  isFlat
                  actions={
                    <ButtonGroup>
                      <Button
                        intent="primary"
                        iconLeft="refresh"
                        value="Refresh Session"
                        onClick={handleSessionRefresh}
                      />
                    </ButtonGroup>
                  }
                >
                  For your security, sessions end after a period of inactivity.
                  Refresh to continue, or sign in again.
                </Alert>
              </Flex>
            </div>
          ) : (
            <MessageRenderer
              streamingMessageId={streamingMessageId}
              messages={messages}
              onExampleClick={setMessage}
              onFeedback={handleFeedback}
              messageFeedback={messageFeedback}
              onRegenerate={handleRegenerate}
              onOpenQuestionsLibrary={() => setIsQuestionsLibraryOpen(true)}
              thinkingStatus={thinkingStatus}
            />
          )}
        </div>

        <div className={styles.inputContainer}>
          <Flex className={styles.inputRow} gap={6} yAlign="end">
            <div className={styles.textareaWrapper}>
              <Textarea
                label="Message"
                rows={1}
                isLabelHidden
                placeholder="Ask me anything about clients, questionnaires, or deadlines..."
                value={message}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                isDisabled={sessionExpired || !isInitialized}
              />
              <button
                className={styles.questionsLibraryButton}
                onClick={() => setIsQuestionsLibraryOpen(true)}
                title="Questions Library"
                disabled={sessionExpired || !isInitialized}
              >
                <Icon name="list" size={20} color="secondary" />
              </button>
            </div>
            <ButtonGroup>
              {isStreaming ? (
                <Button
                  intent="secondary"
                  iconLeft="stop-circle"
                  value="Stop"
                  onClick={handleStop}
                />
              ) : (
                <Button
                  intent="primary"
                  iconLeft="send"
                  isIconOnly={true}
                  value="Send"
                  onClick={handleSend}
                  isDisabled={
                    !message.trim() || sessionExpired || !isInitialized
                  }
                />
              )}
            </ButtonGroup>
          </Flex>

          <Flex
            gap={4}
            isCentered={true}
            className={styles.assistant__disclaimer}
          >
            <Icon name="warning" size={12} color="secondary" />
            <Text type="tiny" color="secondary">
              AI-generated content may be inaccurate
            </Text>
          </Flex>
        </div>
      </div>

      {/* Chat History Popup */}
      {(isHistoryOpen || isAssistantOpen) && (
        <ChatHistoryPopup
          isOpen={isHistoryOpen}
          onClose={() => toggleHistory()}
        />
      )}

      {/* Questions Library Popup */}
      {(isQuestionsLibraryOpen || isAssistantOpen) && (
        <QuestionsLibraryPopup
          isOpen={isQuestionsLibraryOpen}
          onClose={() => setIsQuestionsLibraryOpen(false)}
          onQuestionSelect={setMessage}
        />
      )}

      {/* Delete Session Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className={styles.confirmationOverlay}>
          <div className={styles.confirmationDialog}>
            <Flex isColumn={true} gap={16}>
              <Flex isColumn={true} gap={8}>
                <Heading level={5} color="critical">
                  Delete Session
                </Heading>
                <Text color="secondary">
                  Are you sure you want to delete this chat session? This action
                  cannot be undone and will remove all messages in this
                  conversation.
                </Text>
              </Flex>
              <Flex gap={8} isXCentered={true}>
                <Button
                  value="Cancel"
                  onClick={() => setShowDeleteConfirm(false)}
                  intent="ghost"
                  size="small"
                />
                <Button
                  value="Delete Session"
                  onClick={handleDeleteSession}
                  intent="danger"
                  size="small"
                  iconLeft="trash"
                />
              </Flex>
            </Flex>
          </div>
        </div>
      )}
    </div>
  );
};
